import { getAuthToken } from '../utils/authStorage';

import API_BASE_URL from '../config/apiBaseUrl';
import journalService from './journalService';

const RETRYABLE_SEARCH_STATUSES = new Set([502, 503, 504]);
const SEARCH_RETRY_ATTEMPTS = 1;
const SEARCH_RETRY_DELAY_MS = 350;
const OPENFOODFACTS_PRODUCTS_PROXY_URL = `${API_BASE_URL}/openfoodfacts/products`;
const MISSING_NUTRIMENTS_FALLBACK_MESSAGE = 'Certaines données nutritionnelles sont temporairement indisponibles.';

// Tri par défaut du front. Si true, on n'envoie PAS sortBy quand c'est la valeur
// par défaut : c'est alors le backend qui décide (même comportement que Insomnia
// sans paramètre). Mets false pour retrouver l'ancien comportement (toujours envoyé).
const DEFAULT_SORT = 'unique_scans_n';
const OMIT_DEFAULT_SORT = true;

// Fuzzy : nombre max de variantes testées (au lieu de ~20 requêtes par recherche).
const FUZZY_MAX_VARIANTS = 3;

const asTrimmedString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const extractBrandFromTags = (brandsTags) => {
  if (!Array.isArray(brandsTags)) return '';
  const firstTag = brandsTags.find((tag) => asTrimmedString(tag) !== '');
  if (!firstTag) return '';
  return asTrimmedString(firstTag).replace(/^[a-z]{2}:/i, '').replace(/-/g, ' ');
};

const isUsdaProduct = (product) => (
  asTrimmedString(product?._source).toLowerCase() === 'usda'
  || asTrimmedString(product?._raw_source).toLowerCase().includes('usda')
);

const getPreferredProductName = (product) => {
  if (!product || typeof product !== 'object') {
    return '';
  }

  if (isUsdaProduct(product)) {
    return asTrimmedString(
      product.product_name_fr
      || product.product_name
      || product.generic_name_fr
      || product.generic_name
      || product.name
    );
  }

  return asTrimmedString(
    product.product_name
    || product.product_name_fr
    || product.generic_name
    || product.generic_name_fr
    || product.name
  );
};

const normalizeProductIdentityFields = (product, fallbackCode = '') => {
  if (!product || typeof product !== 'object') {
    return product;
  }

  const normalizedCode = asTrimmedString(
    product.code
    || product._id
    || product.id
    || fallbackCode
  );

  const normalizedName = getPreferredProductName(product);

  const normalizedBrand = asTrimmedString(
    product.brands
    || product.brand
    || product.brand_owner
    || extractBrandFromTags(product.brands_tags)
  );

  return {
    ...product,
    ...(normalizedCode ? { code: normalizedCode } : {}),
    ...(normalizedName ? { product_name: normalizedName } : {}),
    ...(normalizedBrand ? { brands: normalizedBrand } : {}),
  };
};

const normalizeProductsIdentityFields = (products, fallbackCode = '') => {
  if (!Array.isArray(products)) return [];
  return products.map((product) => normalizeProductIdentityFields(product, fallbackCode));
};

const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const extractErrorMessage = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    const parsed = tryParseJson(data.detail);
    const parsedMessage = extractErrorMessage(parsed);
    return parsedMessage || data.detail;
  }

  if (typeof data['hydra:description'] === 'string' && data['hydra:description'].trim()) {
    return data['hydra:description'];
  }

  if (Array.isArray(data.violations) && data.violations.length > 0) {
    const firstViolation = data.violations[0];
    if (firstViolation?.message) {
      return firstViolation.message;
    }
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];
    if (typeof firstError === 'string') return firstError;
    if (firstError?.message) return firstError.message;
  }

  if (data.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).find((value) => value);
    if (typeof firstError === 'string') return firstError;
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  return null;
};

const createAbortError = () => {
  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  return error;
};

const hasNutriments = (product) => {
  const nutr = product?.nutriments;
  return nutr && typeof nutr === 'object' && Object.keys(nutr).length > 0;
};

const delay = (ms, signal) => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(createAbortError());
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      cleanup();
      reject(createAbortError());
    };

    const cleanup = () => {
      if (signal) {
        signal.removeEventListener('abort', onAbort);
      }
    };

    if (signal) {
      signal.addEventListener('abort', onAbort, { once: true });
    }
  });
};

const parseJsonResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = extractErrorMessage(data);
    const error = new Error(message || 'Erreur lors de l\'envoi vers OpenFoodFacts.');
    error.status = response.status;

    const retryAfterHeader = Number(response.headers.get('retry-after'));
    const retryAfterBody = Number(data?.retry_after);
    const retryAfter = Number.isFinite(retryAfterHeader) && retryAfterHeader > 0
      ? retryAfterHeader
      : retryAfterBody;

    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      error.retryAfter = retryAfter;
    }

    throw error;
  }
  return data;
};

const isOffNotFoundError = (error) => {
  const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  return Number(error?.status) === 404
    || (Number(error?.status) === 400 && message.includes('statut 404'));
};

const generateFuzzyQueryVariants = (query) => {
  const normalized = String(query || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, '')
    .trim();

  if (normalized.length < 3) return [];

  const chars = [...normalized];
  const variants = new Set();

  // Substitutions phonétiques FR (les plus utiles en premier)
  const phoneticPairs = [
    ['ph', 'f'], ['f', 'ph'],
    ['c', 'k'], ['k', 'c'],
    ['qu', 'k'],
    ['au', 'o'], ['o', 'au'],
    ['ai', 'e'], ['e', 'ai'],
    ['ou', 'u'], ['u', 'ou'],
    ['an', 'en'], ['en', 'an'],
    ['in', 'ein'], ['ein', 'in'],
    ['eau', 'o'],
    ['ss', 's'], ['s', 'ss'],
    ['tt', 't'], ['ll', 'l'],
    ['y', 'i'], ['i', 'y'],
  ];

  for (const [from, to] of phoneticPairs) {
    if (normalized.includes(from)) {
      variants.add(normalized.replace(from, to));
    }
  }

  // Transposition de deux lettres adjacentes
  for (let i = 0; i < chars.length - 1; i++) {
    const v = [...chars];
    [v[i], v[i + 1]] = [v[i + 1], v[i]];
    variants.add(v.join(''));
  }

  // Suppression d'une lettre
  for (let i = 0; i < chars.length; i++) {
    const v = [...chars.slice(0, i), ...chars.slice(i + 1)].join('');
    if (v.length >= 3) variants.add(v);
  }

  variants.delete(normalized);
  return [...variants].filter(Boolean);
};

const openFoodFactsService = {
  addProduct: async (payload, { mealId, targetDate } = {}) => {
    const response = await fetch(OPENFOODFACTS_PRODUCTS_PROXY_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await parseJsonResponse(response);

    // Ajout automatique au journal si un repas cible est fourni
    if (mealId && data?.product) {
      const product = normalizeProductIdentityFields(data.product, payload.barcode ?? '');
      const nutriments = product.nutriments || {};
      const macronutrients = {
        'energy-kcal_100g': nutriments['energy-kcal_100g'],
        'proteins_100g': nutriments['proteins_100g'],
        'carbohydrates_100g': nutriments['carbohydrates_100g'],
        'fat_100g': nutriments['fat_100g'],
        'saturated-fat_100g': nutriments['saturated-fat_100g'],
        'sugars_100g': nutriments['sugars_100g'],
        'fiber_100g': nutriments['fiber_100g'],
        'salt_100g': nutriments['salt_100g'],
      };

      await journalService.addFoodItem(mealId, {
        name: product.product_name || payload.name,
        quantity: Number(payload.quantity) || 100,
        macronutrients: macronutrients,
        ingredients: product.ingredients_text
          ? product.ingredients_text.split(',').map((s) => s.trim()).filter(Boolean)
          : (payload.ingredients ?? []),
        openFoodFactsData: product,
        targetDate: targetDate ?? new Date().toISOString(),
      });
    }

    return data;
  },
  getProductByCode: async ({ code, signal } = {}) => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) {
      throw new Error('Code-barres requis.');
    }

    try {
      const response = await fetch(`${OPENFOODFACTS_PRODUCTS_PROXY_URL}/${encodeURIComponent(trimmedCode)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Accept': 'application/json',
        },
        signal
      });

      const data = await parseJsonResponse(response);
      const product = data?.product || null;
      return normalizeProductIdentityFields(product, trimmedCode);
    } catch (error) {
      if (isOffNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  },
  async searchFoods({
    query,
    page = 1,
    pageSize = 20,
    filters = {},
    signal,
    allowMissingNutriments = false,
    allowFuzzy = true,
  } = {}) {
    const trimmedQuery = asTrimmedString(query);

    if (!trimmedQuery) {
      throw new Error('Terme de recherche requis.');
    }

    const requestedSort = asTrimmedString(filters.sortBy);

    const apiFilters = {
      sortBy: OMIT_DEFAULT_SORT && requestedSort === DEFAULT_SORT ? '' : requestedSort,
      nutritionGradesTags: filters.nutritionGradesTags || filters.nutriScore,
      categoriesTags: filters.categoriesTags || filters.category,
      labelsTags: filters.labelsTags || filters.label,
    };

    const proxyParams = new URLSearchParams({
      query: trimmedQuery,
      page,
      page_size: pageSize,
      ...Object.entries(apiFilters).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {}),
    });

    // Le fuzzy remplace la requête par une variante : on ne le fait pas quand
    // l'utilisateur a des filtres actifs (0 résultat y est normal, pas une faute de frappe).
    const hasActiveFilters = Boolean(
      apiFilters.nutritionGradesTags || apiFilters.categoriesTags || apiFilters.labelsTags
    );

    let lastError = null;

    for (let attempt = 0; attempt <= SEARCH_RETRY_ATTEMPTS; attempt += 1) {
      if (signal?.aborted) {
        throw createAbortError();
      }

      try {
        const response = await fetch(`${OPENFOODFACTS_PRODUCTS_PROXY_URL}/search?${proxyParams.toString()}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          signal
        });

        let data = await parseJsonResponse(response);

        let products = normalizeProductsIdentityFields(
          Array.isArray(data?.products) ? data.products : [],
          ''
        );
        let correctedQuery = null;

        // Fuzzy retry côté client si 0 résultat (sans filtres, max FUZZY_MAX_VARIANTS requêtes)
        if (
          allowFuzzy
          && !hasActiveFilters
          && products.length === 0
          && attempt === 0
          && trimmedQuery.length >= 4
        ) {
          const fuzzyVariants = generateFuzzyQueryVariants(trimmedQuery).slice(0, FUZZY_MAX_VARIANTS);
          for (const variant of fuzzyVariants) {
            if (signal?.aborted) throw createAbortError();
            const fuzzyParams = new URLSearchParams({ ...Object.fromEntries(proxyParams), query: variant });
            try {
              const fuzzyResp = await fetch(`${OPENFOODFACTS_PRODUCTS_PROXY_URL}/search?${fuzzyParams.toString()}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${getAuthToken()}`,
                  'Accept': 'application/json',
                },
                signal,
              });
              // Rate limit : on arrête tout de suite, inutile d'insister.
              if (fuzzyResp.status === 429) break;
              if (!fuzzyResp.ok) continue;
              const fuzzyData = await fuzzyResp.json().catch(() => ({}));
              const fuzzyProducts = normalizeProductsIdentityFields(
                Array.isArray(fuzzyData?.products) ? fuzzyData.products : [],
                ''
              );
              if (fuzzyProducts.length > 0) {
                products = fuzzyProducts;
                correctedQuery = variant;
                // On réutilise data pour les métadonnées de pagination
                data = { ...data, ...fuzzyData, products: fuzzyProducts };
                break;
              }
            } catch (fuzzyError) {
              // Ne JAMAIS avaler une annulation : sinon la recherche annulée
              // rend un résultat vide qui se retrouve mis en cache.
              if (fuzzyError?.name === 'AbortError' || signal?.aborted) {
                throw createAbortError();
              }
              continue;
            }
          }
        }

        const hasAnyNutriments = products.some((product) => hasNutriments(product));
        const hasProducts = products.length > 0;
        const apiCount = Number(data?.count ?? data?.total ?? 0);
        const computedHasMore =
          Number.isFinite(apiCount) && apiCount > 0
            ? page * pageSize < apiCount
            : products.length >= pageSize
          ;

        if (!allowMissingNutriments && hasProducts && !hasAnyNutriments) {
          if (attempt < SEARCH_RETRY_ATTEMPTS) {
            throw new Error('cgi_missing_nutriments');
          }

          return {
            count: Number(data?.count ?? data?.total ?? products.length),
            page: Number(data?.page ?? page),
            page_size: Number(data?.page_size ?? pageSize),
            page_count: Number(data?.page_count ?? data?.pageCount ?? 0),
            has_more: computedHasMore,
            products,
            degraded: true,
            message: MISSING_NUTRIMENTS_FALLBACK_MESSAGE,
            sources: data?.sources || {},
            ...(correctedQuery ? { correctedQuery } : {})
          };
        }

        return {
          count: Number(data?.count ?? data?.total ?? products.length),
          page: Number(data?.page ?? page),
          page_size: Number(data?.page_size ?? pageSize),
          page_count: Number(data?.page_count ?? data?.pageCount ?? 0),
          has_more: computedHasMore,
          products,
          sources: data?.sources || {},
          ...(correctedQuery ? { correctedQuery } : {})
        };
      } catch (error) {
        lastError = error;
        const retryableStatus = RETRYABLE_SEARCH_STATUSES.has(Number(error?.status));
        const canRetry = retryableStatus
          && attempt < SEARCH_RETRY_ATTEMPTS
          && error?.name !== 'AbortError';

        if (!canRetry && error?.message !== 'cgi_missing_nutriments') {
          throw error;
        }

        await delay(SEARCH_RETRY_DELAY_MS, signal);
      }
    }

    throw lastError || new Error('Erreur lors de la recherche hybride.');
  }
};

export default openFoodFactsService;
